/**
 * 42-serving protein landing page (sections/protein-42-lp.liquid)
 *  - step 1: whey or plant (page accent follows the pick)
 *  - step 2: tap flavor packs into a tray of N slots
 *  - step 3: subscribe or one-time
 *  - live totals, then one cart call and straight to checkout, applying the
 *    promo code through /discount/CODE when the plan qualifies.
 *
 * All prices arrive in the JSON config the section renders from live variants;
 * nothing is fetched. Cart lines use _p_id / _p_id_link like the protein
 * builder so the cart nests the flavor packs under the protein.
 */
(() => {
  const money = (cents) => `$${(Math.round(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const randomId = () => Array.from({ length: 6 }, () => 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]).join('');
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function pop(el, cls = 'is-pop') {
    if (!el || reduceMotion) return;
    el.classList.remove(cls);
    // Force a reflow so the animation restarts on rapid taps.
    void el.offsetWidth;
    el.classList.add(cls);
    el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
  }

  function init(root) {
    if (!root || root.dataset.p42Init) return;
    root.dataset.p42Init = '1';

    let cfg;
    try {
      cfg = JSON.parse(root.querySelector('[data-p42-config]').textContent);
    } catch {
      return;
    }
    const slots = Math.max(1, Number(cfg.slots) || 6);
    const flavors = (cfg.flavors || []).filter((f) => f && f.variantId);
    const storeKey = `p42:${root.dataset.sectionId || 'lp'}`;

    const state = { base: null, flavors: [], plan: cfg.defaultPlan === 'onetime' ? 'onetime' : 'subscribe' };

    // Warm every flavor image once so the first tap fills its slot instantly.
    // The slot <img> is a clone of the decoded image, never a fresh request.
    const preloaded = flavors.map((f) => {
      if (!f.image) return null;
      const img = new Image();
      img.decoding = 'async';
      img.src = f.image;
      if (typeof img.decode === 'function') img.decode().catch(() => {});
      return img;
    });

    // ----- elements
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));
    const baseInputs = $$('[data-p42-base]');
    const planInputs = $$('[data-p42-plan]');
    const flavorCards = $$('[data-p42-flavor]');
    const slotEls = $$('[data-p42-slot]');
    const tray = $('[data-p42-tray]');
    const grid = $('[data-p42-flavors]');
    const count = $('[data-p42-count]');
    const countText = $('[data-p42-count-text]') || count;
    const countIcon = count && count.querySelector('svg');
    const chips = { base: $('[data-p42-chip="base"]'), flavors: $('[data-p42-chip="flavors"]'), plan: $('[data-p42-chip="plan"]') };
    const sum = {
      base: $('[data-p42-sum-base]'),
      flavors: $('[data-p42-sum-flavors]'),
      flavorNote: $('[data-p42-sum-flavor-note]'),
      subRow: $('[data-p42-sum-subrow]'),
      sub: $('[data-p42-sum-sub]'),
      promoRow: $('[data-p42-sum-promorow]'),
      promo: $('[data-p42-sum-promo]'),
      total: $('[data-p42-total]'),
      was: $('[data-p42-was]'),
      save: $('[data-p42-save]'),
      error: $('[data-p42-error]'),
    };
    const bar = $('[data-p42-bar]');
    const barTotal = $('[data-p42-bar-total]');
    const barNote = $('[data-p42-bar-note]');
    const ctas = $$('[data-p42-checkout]');
    const heroTotal = $('[data-p42-hero-total]');
    const heroWas = $('[data-p42-hero-was]');
    const builder = $('#p42-builder') || root;

    // ----- math
    function protein() {
      return state.base && cfg.proteins ? cfg.proteins[state.base] : null;
    }
    function promoFor(plan, subtotal) {
      const p = cfg.promo || {};
      if (!p.code || !p.percent) return 0;
      if (plan !== 'subscribe' && p.applies !== 'both') return 0;
      // Floor so the estimate never undercuts what checkout charges.
      return Math.floor((subtotal * Number(p.percent)) / 100);
    }
    function totals(plan = state.plan, base = protein(), picks = state.flavors) {
      const sub = plan === 'subscribe';
      const basePrice = base ? (sub ? base.subPrice : base.price) : 0;
      const baseFull = base ? base.price : 0;
      const flavorsTotal = picks.reduce((t, i) => t + ((flavors[i] && flavors[i].price) || 0), 0);
      const subtotal = basePrice + flavorsTotal;
      const promo = promoFor(plan, subtotal);
      const total = subtotal - promo;
      const full = baseFull + flavorsTotal;
      return { sub, basePrice, baseFull, flavorsTotal, subtotal, subSavings: baseFull - basePrice, promo, total, full, savings: full - total };
    }
    // Hero "from" price: cheapest available protein with a full tub, subscribed.
    function heroPrice() {
      const bases = Object.values(cfg.proteins || {}).filter((b) => b && b.available && b.variantId);
      if (!bases.length || !flavors.length) return null;
      const cheapest = flavors.reduce((m, f) => Math.min(m, f.price), Infinity);
      const picks = Array.from({ length: slots }, () => flavors.findIndex((f) => f.price === cheapest));
      return bases.map((b) => totals('subscribe', b, picks)).sort((a, b) => a.total - b.total)[0];
    }

    // ----- render
    function render() {
      root.dataset.base = state.base || '';
      baseInputs.forEach((input) => { input.checked = input.value === state.base; });
      planInputs.forEach((input) => { input.checked = input.value === state.plan; });

      const n = state.flavors.length;
      const full = n >= slots;
      const qty = new Map();
      state.flavors.forEach((i) => qty.set(i, (qty.get(i) || 0) + 1));

      flavorCards.forEach((card) => {
        const i = Number(card.dataset.p42Flavor);
        const q = qty.get(i) || 0;
        const main = card.querySelector('.p42-flavor-main');
        const badge = card.querySelector('[data-p42-qty]');
        const price = card.querySelector('[data-p42-price]');
        const stepper = card.querySelector('[data-p42-stepper]');
        const qtyText = card.querySelector('[data-p42-qty-text]');
        const plus = stepper && stepper.querySelector('[data-p42-add]');
        card.classList.toggle('is-picked', q > 0);
        if (main) main.setAttribute('aria-pressed', q > 0 ? 'true' : 'false');
        if (badge) { badge.hidden = q === 0; badge.textContent = `×${q}`; }
        if (price) price.hidden = q > 0;
        if (stepper) stepper.hidden = q === 0;
        if (qtyText) qtyText.textContent = q === 1 ? '1 pack' : `${q} packs`;
        if (plus) plus.disabled = full;
      });
      if (grid) grid.classList.toggle('is-full', full);

      slotEls.forEach((slot, pos) => {
        const i = state.flavors[pos];
        const f = i === undefined ? null : flavors[i];
        slot.classList.toggle('is-filled', !!f);
        slot.disabled = !f;
        slot.innerHTML = '';
        if (f) {
          slot.style.setProperty('--swatch', f.swatch || '');
          if (f.image) {
            const src = preloaded[i];
            const img = src ? src.cloneNode() : document.createElement('img');
            if (!src) img.src = f.image;
            img.alt = '';
            slot.appendChild(img);
          }
          slot.setAttribute('aria-label', `Slot ${pos + 1}: ${f.title}. Remove`);
          slot.title = `${f.title} (tap to remove)`;
        } else {
          slot.style.removeProperty('--swatch');
          slot.textContent = String(pos + 1);
          slot.setAttribute('aria-label', `Slot ${pos + 1}, empty`);
          slot.removeAttribute('title');
        }
      });

      if (count) {
        countText.textContent = full ? cfg.text.complete : `${n} of ${slots}`;
        if (countIcon) countIcon.style.display = full ? '' : 'none';
        count.classList.toggle('is-complete', full);
      }

      // Step chips
      const baseDone = !!protein();
      if (chips.base) { chips.base.classList.toggle('is-done', baseDone); chips.base.classList.toggle('is-active', !baseDone); }
      if (chips.flavors) { chips.flavors.classList.toggle('is-done', full); chips.flavors.classList.toggle('is-active', baseDone && !full); }
      if (chips.plan) { chips.plan.classList.toggle('is-done', baseDone && full); chips.plan.classList.toggle('is-active', baseDone && full); }

      // Plan card prices reflect the current pick
      ['subscribe', 'onetime'].forEach((plan) => {
        const t = totals(plan);
        const priceEl = root.querySelector(`[data-p42-plan-price="${plan}"]`);
        const wasEl = root.querySelector(`[data-p42-plan-was="${plan}"]`);
        const ready = baseDone;
        if (priceEl) priceEl.textContent = ready ? money(t.total) : '';
        if (wasEl) wasEl.textContent = ready && t.total < t.full ? money(t.full) : '';
      });

      // Summary
      const t = totals();
      const base = protein();
      if (sum.base) sum.base.textContent = base ? `${base.label} · ${money(t.basePrice)}` : cfg.text.noBase;
      if (sum.flavors) sum.flavors.textContent = n ? `${n} × ${money(flavors[state.flavors[0]] ? t.flavorsTotal / n : 0)} = ${money(t.flavorsTotal)}` : cfg.text.noFlavors;
      if (sum.flavorNote) {
        const names = [];
        qty.forEach((q, i) => names.push(q > 1 ? `${flavors[i].title} ×${q}` : flavors[i].title));
        sum.flavorNote.textContent = names.join(', ');
      }
      if (sum.subRow) { sum.subRow.hidden = !(t.sub && base && t.subSavings > 0); if (sum.sub) sum.sub.textContent = `−${money(t.subSavings)}`; }
      if (sum.promoRow) { sum.promoRow.hidden = !(t.promo > 0); if (sum.promo) sum.promo.textContent = `−${money(t.promo)}`; }
      if (sum.total) sum.total.textContent = base ? money(t.total) : '—';
      if (sum.was) { sum.was.hidden = !(base && t.total < t.full); sum.was.textContent = money(t.full); }
      if (sum.save) { sum.save.hidden = !(t.savings > 0 && base); sum.save.textContent = `You save ${money(t.savings)}`; }

      // CTA copy
      let label;
      if (!base) label = cfg.text.ctaBase;
      else if (!full) {
        const left = slots - n;
        label = cfg.text.ctaFlavors.replace('[n]', String(left));
        if (left === 1) label = label.replace(/packs\b/, 'pack');
      }
      else label = `${cfg.text.ctaReady} · ${money(t.total)}`;
      // The mobile bar has less room: same states, shorter words.
      let barLabel = label;
      if (base && !full) barLabel = `Add ${slots - n} more`;
      else if (base && full) barLabel = `${cfg.text.ctaReady} · ${money(t.total)}`;
      ctas.forEach((btn) => { if (!btn.dataset.busy) btn.textContent = bar && bar.contains(btn) ? barLabel : label; });

      if (barTotal) barTotal.textContent = base ? money(t.total) : '';
      if (barNote) barNote.textContent = base ? (full ? (t.savings > 0 ? `You save ${money(t.savings)}` : '') : `${n} of ${slots} flavors`) : `${n} of ${slots} flavors`;

      try { sessionStorage.setItem(storeKey, JSON.stringify(state)); } catch { /* private mode */ }
    }

    function showError(msg) {
      if (!sum.error) return;
      sum.error.textContent = msg;
      sum.error.hidden = !msg;
    }

    function flash(stepName) {
      const step = root.querySelector(`[data-p42-step="${stepName}"]`);
      if (!step) return;
      step.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      pop(step, 'is-flash');
    }

    // ----- actions
    function setBase(key) {
      if (!cfg.proteins || !cfg.proteins[key] || !cfg.proteins[key].available) return;
      state.base = key;
      showError('');
      render();
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'p42_pick_protein', protein: key });
    }
    function addFlavor(i) {
      if (!flavors[i] || !flavors[i].available) return;
      if (state.flavors.length >= slots) { pop(tray, 'is-shake'); return; }
      state.flavors.push(i);
      render();
      pop(slotEls[state.flavors.length - 1]);
      const card = flavorCards.find((c) => Number(c.dataset.p42Flavor) === i);
      pop(card && card.querySelector('[data-p42-qty]'));
      if (state.flavors.length === slots) pop(count);
    }
    function removeFlavor(i) {
      const pos = state.flavors.lastIndexOf(i);
      if (pos > -1) { state.flavors.splice(pos, 1); render(); }
    }
    function removeAt(pos) {
      if (pos < state.flavors.length) { state.flavors.splice(pos, 1); render(); }
    }
    function surprise() {
      const pool = flavors.map((f, i) => (f.available ? i : -1)).filter((i) => i > -1);
      if (!pool.length) return;
      const picks = [];
      const bag = pool.slice();
      while (picks.length < slots) {
        if (!bag.length) bag.push(...pool);
        picks.push(bag.splice(Math.floor(Math.random() * bag.length), 1)[0]);
      }
      state.flavors = picks;
      render();
      slotEls.forEach((s, idx) => setTimeout(() => pop(s), idx * 60));
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'p42_surprise_me' });
    }

    async function checkout(btn) {
      if (btn.dataset.busy) return;
      showError('');
      const base = protein();
      if (!base) { flash('base'); return; }
      if (state.flavors.length < slots) { flash('flavors'); return; }
      const sub = state.plan === 'subscribe';
      if (sub && !base.planId) { showError(cfg.text.noPlan); flash('plan'); return; }

      const pid = randomId();
      const items = [{ id: base.variantId, quantity: 1, properties: { _source: cfg.source, _p_id: pid } }];
      if (sub) items[0].selling_plan = base.planId;
      const grouped = new Map();
      state.flavors.forEach((i) => grouped.set(i, (grouped.get(i) || 0) + 1));
      grouped.forEach((q, i) => {
        const f = flavors[i];
        const line = { id: f.variantId, quantity: q, properties: { _source: cfg.source, _p_id_link: pid } };
        if (q > 1) line.properties._p_quantity = String(q);
        if (sub && f.planId) line.selling_plan = f.planId;
        items.push(line);
      });

      const t = totals();
      const label = btn.textContent;
      ctas.forEach((b) => { b.dataset.busy = '1'; b.classList.add('is-loading'); b.textContent = 'One sec…'; });
      try {
        if (cfg.clearCart) {
          await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
        }
        let ok = false;
        if (window._cart && typeof window._cart.add === 'function') {
          const res = await window._cart.add({ items });
          ok = !(res && res.cart_error);
        } else {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          ok = r.ok;
        }
        if (!ok) throw new Error('cart_error');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'p42_checkout',
          protein: state.base,
          purchase_type: sub ? 'subscription' : 'onetime',
          flavors: state.flavors.map((i) => flavors[i].title),
          value: t.total / 100,
          promo_code: t.promo > 0 ? cfg.promo.code : '',
        });
        try { sessionStorage.removeItem(storeKey); } catch { /* ignore */ }
        const code = t.promo > 0 ? cfg.promo.code : '';
        window.location.href = code
          ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/checkout')}`
          : '/checkout';
      } catch {
        showError(cfg.text.cartError);
        ctas.forEach((b) => { delete b.dataset.busy; b.classList.remove('is-loading'); b.textContent = label; });
      }
    }

    // ----- wiring
    baseInputs.forEach((input) => input.addEventListener('change', () => setBase(input.value)));
    $$('[data-p42-pick]').forEach((btn) => btn.addEventListener('click', () => {
      setBase(btn.dataset.p42Pick);
      builder.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }));
    $$('[data-p42-scroll]').forEach((a) => a.addEventListener('click', (e) => {
      e.preventDefault();
      builder.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }));
    planInputs.forEach((input) => input.addEventListener('change', () => {
      state.plan = input.value === 'onetime' ? 'onetime' : 'subscribe';
      showError('');
      render();
    }));
    flavorCards.forEach((card) => {
      const i = Number(card.dataset.p42Flavor);
      card.querySelectorAll('[data-p42-add]').forEach((btn) => btn.addEventListener('click', () => addFlavor(i)));
      card.querySelectorAll('[data-p42-remove]').forEach((btn) => btn.addEventListener('click', () => removeFlavor(i)));
    });
    slotEls.forEach((slot) => slot.addEventListener('click', () => removeAt(Number(slot.dataset.p42Slot))));
    const surpriseBtn = $('[data-p42-surprise]');
    if (surpriseBtn) surpriseBtn.addEventListener('click', surprise);
    const clearBtn = $('[data-p42-clear]');
    if (clearBtn) clearBtn.addEventListener('click', () => { state.flavors = []; render(); });
    ctas.forEach((btn) => btn.addEventListener('click', () => checkout(btn)));

    // ----- "More details" side panels (one per protein)
    const backdrop = $('[data-p42-backdrop]');
    let openPanel = null;
    let lastTrigger = null;
    function closePanel() {
      if (!openPanel) return;
      const panel = openPanel;
      openPanel = null;
      panel.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('p42-lock');
      const done = () => { panel.hidden = true; if (backdrop) backdrop.hidden = true; };
      if (reduceMotion) done(); else setTimeout(done, 300);
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus({ preventScroll: true });
    }
    function openDetails(key, trigger) {
      const panel = root.querySelector(`[data-p42-panel="${key}"]`);
      if (!panel) return;
      if (openPanel && openPanel !== panel) { openPanel.classList.remove('is-open'); openPanel.hidden = true; }
      lastTrigger = trigger || null;
      openPanel = panel;
      panel.hidden = false;
      if (backdrop) backdrop.hidden = false;
      document.body.classList.add('p42-lock');
      // Next frame so the slide-in transition runs from the hidden state.
      requestAnimationFrame(() => {
        panel.classList.add('is-open');
        if (backdrop) backdrop.classList.add('is-open');
        // preventScroll: focusing inside the still-translated fixed panel would
        // otherwise scroll the document to the panel's in-flow position.
        const close = panel.querySelector('[data-p42-panel-close]');
        if (close) close.focus({ preventScroll: true });
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'p42_details_open', protein: key });
    }
    $$('[data-p42-details]').forEach((btn) => btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openDetails(btn.dataset.p42Details, btn);
    }));
    $$('[data-p42-panel-close]').forEach((btn) => btn.addEventListener('click', closePanel));
    if (backdrop) backdrop.addEventListener('click', closePanel);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openPanel) closePanel(); });
    $$('[data-p42-panel-pick]').forEach((btn) => btn.addEventListener('click', () => {
      const key = btn.dataset.p42PanelPick;
      closePanel();
      setBase(key);
      const step = root.querySelector('[data-p42-step="flavors"]');
      if (step) setTimeout(() => step.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }), reduceMotion ? 0 : 320);
    }));

    // Carousel: scroll-snap track, arrows and dots.
    $$('[data-p42-carousel]').forEach((car) => {
      const track = car.querySelector('[data-p42-carousel-track]');
      const dots = Array.from(car.querySelectorAll('[data-p42-carousel-dot]'));
      if (!track) return;
      const slideW = () => track.clientWidth || 1;
      const index = () => Math.round(track.scrollLeft / slideW());
      const go = (i) => {
        const n = track.children.length;
        const to = ((i % n) + n) % n;
        track.scrollTo({ left: to * slideW(), behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      const prev = car.querySelector('[data-p42-carousel-prev]');
      const next = car.querySelector('[data-p42-carousel-next]');
      if (prev) prev.addEventListener('click', () => go(index() - 1));
      if (next) next.addEventListener('click', () => go(index() + 1));
      dots.forEach((dot) => dot.addEventListener('click', () => go(Number(dot.dataset.p42CarouselDot))));
      let raf = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = index();
          dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.p42CarouselDot) === i));
        });
      }, { passive: true });
    });

    // Ingredient tabs.
    $$('[data-p42-ingredients]').forEach((box) => {
      const tabs = Array.from(box.querySelectorAll('[data-p42-ing-tab]'));
      const panels = Array.from(box.querySelectorAll('[data-p42-ing-panel]'));
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        const i = tab.dataset.p42IngTab;
        tabs.forEach((t) => { const on = t.dataset.p42IngTab === i; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on ? 'true' : 'false'); });
        panels.forEach((p) => { p.hidden = p.dataset.p42IngPanel !== i; });
      }));
    });

    // Sticky bar shows while the builder is on screen (mobile only via CSS).
    if (bar && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => bar.classList.toggle('is-visible', e.isIntersecting));
      }, { rootMargin: '-120px 0px 0px 0px' });
      io.observe(builder);
    }

    // Hero "from" price
    const hp = heroPrice();
    if (hp && heroTotal) {
      heroTotal.textContent = money(hp.total);
      if (heroWas) heroWas.textContent = hp.total < hp.full ? money(hp.full) : '';
    } else if (heroTotal && heroTotal.parentElement) {
      heroTotal.parentElement.hidden = true;
    }

    // Restore a previous visit in this tab, then let the URL override:
    //   ?base=whey|plant  ?plan=subscribe|onetime
    try {
      const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
      if (saved && typeof saved === 'object') {
        if (saved.base && cfg.proteins[saved.base] && cfg.proteins[saved.base].available) state.base = saved.base;
        if (Array.isArray(saved.flavors)) state.flavors = saved.flavors.filter((i) => flavors[i] && flavors[i].available).slice(0, slots);
        if (saved.plan === 'onetime' || saved.plan === 'subscribe') state.plan = saved.plan;
      }
    } catch { /* ignore */ }
    const params = new URLSearchParams(window.location.search);
    const qBase = (params.get('base') || params.get('protein') || '').toLowerCase();
    if (cfg.proteins[qBase] && cfg.proteins[qBase].available) state.base = qBase;
    const qPlan = (params.get('plan') || '').toLowerCase();
    if (qPlan === 'onetime' || qPlan === 'subscribe') state.plan = qPlan;

    render();
  }

  const boot = () => document.querySelectorAll('[data-section-type="protein-42-lp"]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target && e.target.querySelector('[data-section-type="protein-42-lp"]');
    if (root) init(root);
  });
})();
